import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { QuickAnswer } from './QuickAnswer';
import { FAQSection, FAQ, buildFAQSchema } from './FAQSection';
import { RelatedArticles } from './RelatedArticles';
import { ARTICLES_BY_SLUG, CATEGORY_LABELS } from '../../config/blogArticles';

interface BlogArticleLayoutProps {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  quickAnswer: React.ReactNode;
  quickAnswerText: string;
  faqs: FAQ[];
  related: string[];
  ctaText?: string;
  ctaSubtext?: string;
  ctaHref?: string;
  ctaLabel?: string;
  children: React.ReactNode;
}

export function BlogArticleLayout({
  slug,
  title,
  description,
  keywords,
  quickAnswer,
  quickAnswerText,
  faqs,
  related,
  ctaText = 'Put it into practice',
  ctaSubtext = "Play today's stock puzzle and apply what you just learned with real financial data.",
  ctaHref = '/popular',
  ctaLabel = 'Play Wallstreetle Free',
  children,
}: BlogArticleLayoutProps) {
  const meta = ARTICLES_BY_SLUG[slug];
  const url = `https://wallstreetle.com/blog/${slug}`;
  const datePublished = meta?.datePublished ?? '2026-05-04';
  const dateModified = meta?.dateModified ?? datePublished;
  const categoryLabel = meta ? CATEGORY_LABELS[meta.category] : 'Article';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: quickAnswerText || description,
    url,
    datePublished,
    dateModified,
    author: {
      '@type': 'Organization',
      name: 'Wallstreetle',
      url: 'https://wallstreetle.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Wallstreetle',
      url: 'https://wallstreetle.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wallstreetle.com/logo512.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    image: 'https://wallstreetle.com/assets/share-link-image.png',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://wallstreetle.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://wallstreetle.com/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: meta?.shortTitle ?? title,
        item: url,
      },
    ],
  };

  const faqSchema = faqs.length > 0 ? buildFAQSchema(faqs) : null;

  const ogTitle = `${title} | Wallstreetle`;

  return (
    <>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={url} />

        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />
        <meta property="article:published_time" content={datePublished} />
        <meta property="article:author" content="Wallstreetle" />
        <meta property="article:section" content={categoryLabel} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={ogTitle} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <article className="max-w-3xl mx-auto py-10 sm:py-14 px-4">
        <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">{meta?.shortTitle ?? title}</span>
        </nav>

        <header className="mb-8">
          <div className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider mb-4">
            {categoryLabel}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-foreground leading-tight">
            {title}
          </h1>
        </header>

        <QuickAnswer>{quickAnswer}</QuickAnswer>

        <div className="prose dark:prose-invert max-w-none prose-headings:font-serif prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-lg prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
          {children}
        </div>

        <div className="mt-14">
          <FAQSection faqs={faqs} />
        </div>

        <RelatedArticles slugs={related} />

        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">{ctaText}</h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">{ctaSubtext}</p>
          <Link
            to={ctaHref}
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            {ctaLabel}
          </Link>
        </Card>
      </article>
    </>
  );
}
